import { collection, doc, getDocs, onSnapshot, setDoc, type QueryDocumentSnapshot } from 'firebase/firestore'
import type { QrCode } from '../../types'
import { dispatchCatalogEvent, QR_CODES_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import {
  createQrCodeRecord,
  findActiveCodeForTable,
  generateUniqueToken,
  type QrCode as DomainQrCode,
} from '@shared/lib/qrCodeDomain'
import { getTables } from './tables'
import { stripUndefined } from './stripUndefined'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'

const qrCodesCacheByPlace: Record<string, QrCode[]> = {}
let qrCodesUnsubscribe: (() => void) | null = null

function qrCodesRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'qrCodes')
}

function qrCodeDoc(placeId: string, id: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'qrCodes', id)
}

function dispatchQrCodesUpdated(): void {
  dispatchCatalogEvent(QR_CODES_UPDATED_EVENT)
}

function docToQrCode(d: QueryDocumentSnapshot): QrCode {
  const data = d.data()
  return {
    id: d.id,
    token: String(data.token ?? ''),
    tableId: String(data.tableId ?? ''),
    tableLabel: String(data.tableLabel ?? ''),
    status: data.status === 'inactive' ? 'inactive' : 'active',
    createdAt: String(data.createdAt ?? new Date().toISOString()),
    retiredAt: data.retiredAt != null ? String(data.retiredAt) : undefined,
  }
}

function generateQrCodeId(): string {
  return `QR-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function tableShouldHaveQr(table: { qrEnabled?: boolean }): boolean {
  return table.qrEnabled !== false
}

async function migrateTablesToQrCodes(placeId: string, list: QrCode[]): Promise<QrCode[]> {
  const tables = getTables()
  const next = [...list]
  let changed = false
  for (const table of tables) {
    if (!tableShouldHaveQr(table)) continue
    if (findActiveCodeForTable(next as DomainQrCode[], table.id)) continue
    const token = table.qrToken ?? table.id
    const record = createQrCodeRecord({
      id: generateQrCodeId(),
      token,
      tableId: table.id,
      tableLabel: table.label,
    })
    await setDoc(qrCodeDoc(placeId, record.id), record)
    next.push(record)
    changed = true
  }
  return changed ? next : list
}

export async function primeQrCodesCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (qrCodesUnsubscribe) {
    qrCodesUnsubscribe()
    qrCodesUnsubscribe = null
  }
  const snap = await getDocs(qrCodesRef(placeId))
  let list = snap.docs.map((d) => docToQrCode(d))
  list = await migrateTablesToQrCodes(placeId, list)
  qrCodesCacheByPlace[placeId] = list
  qrCodesUnsubscribe = onSnapshot(qrCodesRef(placeId), (snapshot) => {
    qrCodesCacheByPlace[placeId] = snapshot.docs.map((d) => docToQrCode(d))
    dispatchQrCodesUpdated()
  })
}

export function getQrCodes(): QrCode[] {
  const placeId = getCurrentPlaceId()
  return qrCodesCacheByPlace[placeId] ?? []
}

export function saveQrCodes(codes: QrCode[]): void {
  const placeId = getCurrentPlaceId()
  qrCodesCacheByPlace[placeId] = codes
  dispatchQrCodesUpdated()
  ;(async () => {
    const snap = await getDocs(qrCodesRef(placeId))
    const ids = new Set(codes.map((c) => c.id))
    const ops: BatchWriteOp[] = []
    for (const d of snap.docs) {
      if (!ids.has(d.id)) ops.push({ type: 'delete', ref: d.ref })
    }
    for (const c of codes) {
      ops.push({
        type: 'set',
        ref: qrCodeDoc(placeId, c.id),
        data: stripUndefined(c) as object,
      })
    }
    await commitWriteOps(ops)
  })().catch((e) => console.error('Firestore saveQrCodes failed', e))
}

export function upsertQrCode(code: QrCode): QrCode[] {
  const placeId = getCurrentPlaceId()
  const cache = qrCodesCacheByPlace[placeId] ?? []
  const idx = cache.findIndex((c) => c.id === code.id)
  const next = idx === -1 ? [...cache, code] : cache.map((c) => (c.id === code.id ? code : c))
  qrCodesCacheByPlace[placeId] = next
  dispatchQrCodesUpdated()
  setDoc(qrCodeDoc(placeId, code.id), stripUndefined(code)).catch((e) =>
    console.error('Firestore upsertQrCode failed', e),
  )
  return next
}

export function addQrCodeRecord(code: Omit<QrCode, 'id'> & { id?: string }): QrCode {
  const placeId = getCurrentPlaceId()
  const cache = qrCodesCacheByPlace[placeId] ?? []
  const token = code.token || generateUniqueToken(cache as DomainQrCode[])
  const full: QrCode = {
    ...code,
    id: code.id ?? generateQrCodeId(),
    token,
    status: code.status ?? 'active',
    createdAt: code.createdAt ?? new Date().toISOString(),
  }
  upsertQrCode(full)
  return full
}

export function isQrCodesPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in qrCodesCacheByPlace
}

export function clearQrCodesCache(): void {
  if (qrCodesUnsubscribe) {
    qrCodesUnsubscribe()
    qrCodesUnsubscribe = null
  }
  Object.keys(qrCodesCacheByPlace).forEach((k) => delete qrCodesCacheByPlace[k])
}
